import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useWebCourses, useWebCoursesForms, useWebCourseFAQs } from '../hooks/sectionHooks';
import Header from '../components/Header';
import Footer from '../components/Footer';
import LoadingSpinner from '../components/LoadingSpinner';
import '../App.css';

function CourseDetails() {
  const [activeTab, setActiveTab] = useState('intro');
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: webCoursesData, isLoading: courseLoading } = useWebCourses();
  const { data: formsData, isLoading: formsLoading } = useWebCoursesForms();
  const { data: faqsData, isLoading: faqsLoading } = useWebCourseFAQs(id);

  // Find the course by id
  const course = Array.isArray(webCoursesData) 
    ? webCoursesData.find(c => c.id === parseInt(id))
    : null;

  // Find the form for this course
  const courseForm = Array.isArray(formsData)
    ? formsData.find(form => form.web_course_id === parseInt(id))
    : null;

  if (courseLoading) {
    return (
      <div className="App">
        <Header />
        <main className="course-details-page">
          <LoadingSpinner />
        </main>
        <Footer />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="App">
        <Header />

        <main className="course-details-page">
          <div className="course-details-container">
            <h1>Course not found</h1>
            <Link to="/" className="back-button">← Back to Home</Link>
          </div>
        </main>

        <Footer />
      </div>
    );
  }

  return (
    <div className="App">
      <Header />

      {/* Main Content */}
      <main className="course-details-page-new">
        <div className="course-details-wrapper">
          {/* Left Content */}
          <div className="course-left-content">
            <h1 className="course-title-new">{course.name}</h1>
            
            {course.intro_text && (
              <div className="course-short-desc" dangerouslySetInnerHTML={{ __html: course.intro_text.substring(0, 200) + '...' }} />
            )}

            {/* Feature Badges */}
            <div className="course-features">
              <div className="feature-item">
                <span className="feature-icon">🎥</span>
                <span className="feature-text">Цахим хичээл</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">📚</span>
                <span className="feature-text">Сургалтын материал</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">👥</span>
                <span className="feature-text">Идэвхтэй хамт олон</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">💬</span>
                <span className="feature-text">1-1 зөвлөгөө</span>
              </div>
            </div>

            {/* Instructor */}
            <div className="course-instructor">
              <div className="instructor-avatar">
                <img src="https://ui-avatars.com/api/?name=ICER&background=B31B1B&color=fff&size=60" alt="Instructor" />
              </div>
              <div className="instructor-info">
                <h4 className="instructor-name">ICER</h4>
                <span className="instructor-role">БОЛОВСРОЛЫН БАЙГУУЛЛАГА</span>
              </div>
            </div>

            {/* Tabs */}
            <nav className="course-tabs-new">
              <button 
                onClick={() => setActiveTab('intro')} 
                className={`course-tab-new ${activeTab === 'intro' ? 'active' : ''}`}
              >
                Танилцуулга
              </button>
              {course.course_details && (
                <button 
                  onClick={() => setActiveTab('curriculum')} 
                  className={`course-tab-new ${activeTab === 'curriculum' ? 'active' : ''}`}
                >
                  Хөтөлбөр
                </button>
              )}
              <button 
                onClick={() => setActiveTab('materials')} 
                className={`course-tab-new ${activeTab === 'materials' ? 'active' : ''}`}
              >
                Сургалтын материал
              </button>
              <button 
                onClick={() => setActiveTab('faq')} 
                className={`course-tab-new ${activeTab === 'faq' ? 'active' : ''}`}
              >
                Түгээмэл асуултууд
              </button>
            </nav>

            {/* Tab Content */}
            <div className="course-tab-content">
              {activeTab === 'intro' && (
                <section className="course-section-new">
                  <h2>Танилцуулга</h2>
                  <div dangerouslySetInnerHTML={{ __html: course.intro_text }} />
                </section>
              )}

              {activeTab === 'curriculum' && course.course_details && (
                <section className="course-section-new">
                  <h2>Хөтөлбөр</h2>
                  <div dangerouslySetInnerHTML={{ __html: course.course_details }} />
                </section>
              )}

              {activeTab === 'materials' && (
                <section className="course-section-new">
                  <h2>Сургалтын материал</h2>
                  <div dangerouslySetInnerHTML={{ __html: course.course_materials || '<p>Сургалтын материал хараахан нэмэгдээгүй байна.</p>' }} />
                  
                  {/* Gallery */}
                  {course.gallery && course.gallery.length > 0 && (
                    <div className="course-gallery">
                      <div className="gallery-grid">
                        {course.gallery.map((image, index) => (
                          <div key={index} className="gallery-item">
                            <img src={image} alt={`Gallery ${index + 1}`} />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </section>
              )}

              {activeTab === 'faq' && (
                <section className="course-section-new">
                  <h2>Түгээмэл асуултууд</h2>
                  {faqsLoading ? (
                    <LoadingSpinner />
                  ) : faqsData && faqsData.length > 0 ? (
                    <div className="faq-list">
                      {faqsData.map((faq) => (
                        <div key={faq.id} className="faq-item">
                          <div className="faq-question">
                            <span className="faq-icon">❓</span>
                            <h4>{faq.question}</h4>
                          </div>
                          <div className="faq-answer">
                            <p>{faq.answer}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p>Түгээмэл асуултууд хараахан нэмэгдээгүй байна.</p>
                  )}
                </section>
              )}
            </div>
          </div>

          {/* Right Sidebar */}
          <aside className="course-sidebar-new">
            {/* Video Preview Card */}
            <div className="course-preview-card">
              {course.intro_video ? (
                <div className="preview-video">
                  <iframe
                    src={course.intro_video.replace('watch?v=', 'embed/')}
                    title="Course Introduction Video"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              ) : (
                <div className="preview-placeholder">
                  <div className="play-button">
                    <span>▶</span>
                  </div>
                </div>
              )}

              {/* Price */}
              <div className="course-price-section">
                {course.price && (
                  <>
                    <span className=" ">{`${course.price.toLocaleString()}₮`}</span>
                    {course.original_price && (
                      <span className="original-price">{course.original_price.toLocaleString()}₮</span>
                    )}
                  </>
                )}
              </div>

              {/* Register Button */}
              {formsLoading ? (
                <LoadingSpinner className="loading-indicator-compact" />
              ) : courseForm ? (
                <button 
                  onClick={() => navigate(`/course/${id}/form/${courseForm.id}`, { state: { courseName: course.name } })}
                  className="add-to-cart-btn"
                >
                  БҮРТГҮҮЛЭХ
                </button>
              ) : course.registration_link ? (
                <a href={course.registration_link} className="add-to-cart-btn" target="_blank" rel="noopener noreferrer">
                  БҮРТГҮҮЛЭХ
                </a>
              ) : null}

              {/* Course Info List */}
              <ul className="course-info-list">
                {course.duration && (
                  <li>
                    <span className="info-icon">⏱</span>
                    <span className="info-label">Хугацаа:</span>
                    <span className="info-value">{course.duration}</span>
                  </li>
                )}
                <li>
                  <span className="info-icon">📊</span>
                  <span className="info-label">Түвшин:</span>
                  <span className="info-value">{course.level || 'Бүх түвшин'}</span>
                </li>
                <li>
                  <span className="info-icon">👥</span>
                  <span className="info-label">Суралцагчдын тоо:</span>
                  <span className="info-value">{course.students || '18-24'}</span>
                </li>
                <li>
                  <span className="info-icon">🎥</span>
                  <span className="info-label">Цахим хичээл:</span>
                  <span className="info-value">{course.online_class || 'Тийм'}</span>
                </li>
                <li>
                  <span className="info-icon">📝</span>
                  <span className="info-label">Жишиг шалгалт:</span>
                  <span className="info-value">{course.mock_exams || '2+'}</span>
                </li>
                <li>
                  <span className="info-icon">🏆</span>
                  <span className="info-label">Сертификат:</span>
                  <span className="info-value">{course.certificate || 'Тийм'}</span>
                </li>
                {/* <li>
                  <span className="info-icon">♾️</span>
                  <span className="info-label">Хандалт:</span>
                  <span className="info-value">Насан туршийн</span>
                </li> */}
                <li>
                  <span className="info-icon">💻</span>
                  <span className="info-label">Төхөөрөмж:</span>
                  <span className="info-value">Компьютер, таблет, гар утас</span>
                </li>
              </ul>
            </div>

            {/* Schedule Card */}
            {course.schedule && (
              <div className="schedule-card-new">
                <h3>Хичээлийн хуваарь</h3>
                <p>Элсэлтээсээ хамаарч:</p>
                <ul className="course-schedule-list">
                  <li>Даваа, Лхагва /18:50-21:00/, Бямба /10:00-16:00/</li>
                  <li>Мягмар, Пүрэв /18:50-21:00/, Ням /10:00-16:00/ гарагуудад хичээллэнэ.</li>
                </ul>
              </div>
            )}
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default CourseDetails;
